import {
  API_CALL_FAILURE,
  API_CALL_REQUEST,
  API_CALL_SUCCESS,
  CREATE_SEARCH,
  CREATE_SEARCH_SESSION,
  TYPEAHEAD_STREAMING_START,
  TYPEAHEAD_STREAMING_UPDATE,
  TYPEAHEAD_STREAMING_COMPLETE,
  TYPEAHEAD_STREAMING_ERROR,
  SET_TYPEAHEAD_STREAMING_SOCKET_STATUS,
  SET_TYPEAHEAD_USER_SELECTED_DATES,
  SET_TYPEAHEAD_USER_SELECTED_OCCUPANCY,
} from 'actions/actionConstants'
import { FETCH_RECOMMENDATION_SUGGESTIONS } from 'actions/apiActionConstants'
import { apiReducerSwitch, createAppReducer } from 'lib/redux/reducerUtils'

export const initialSearchState: App.SearchState = {
  searches: {},
  searchSessions: {},
  typeaheadStreamingData: {},
  recommendationSuggestions: {},
}

const apiCalls = apiReducerSwitch<App.SearchState>({
  [FETCH_RECOMMENDATION_SUGGESTIONS]: {
    success: (state, action) => ({
      recommendationSuggestions: {
        ...state.recommendationSuggestions,
        [action.key]: action.data,
      },
    }),
  },
})

export default createAppReducer<App.SearchState>(initialSearchState, {
  [API_CALL_REQUEST]: (state, action) => apiCalls(state, action),
  [API_CALL_SUCCESS]: (state, action) => apiCalls(state, action),
  [API_CALL_FAILURE]: (state, action) => apiCalls(state, action),
  [CREATE_SEARCH]: (state, action) => {
    const { key, searchId, searchContext, searchEvent } = action

    return {
      ...state,
      searches: {
        ...state.searches,
        [key]: {
          searchId,
          searchContext: searchContext ?? state.searches[key]?.searchContext,
          searchEvent,
        },
      },
    }
  },
  [CREATE_SEARCH_SESSION]: (state, action) => {
    const { key, searchSessionId } = action

    return {
      ...state,
      searchSessions: {
        ...state.searchSessions,
        [key]: { searchSessionId },
      },
    }
  },
  [TYPEAHEAD_STREAMING_START]: (state, action) => {
    const { typeaheadId } = action
    const currentData = state.typeaheadStreamingData[typeaheadId]

    return {
      ...state,
      typeaheadStreamingData: {
        ...state.typeaheadStreamingData,
        [typeaheadId]: {
          ...currentData,
          typeaheadId,
          isComplete: false,
          completedInput: undefined,
        },
      },
    }
  },
  [TYPEAHEAD_STREAMING_UPDATE]: (state, action) => {
    const { typeaheadId, updates, timestamp, input, extractedDataType } = action
    const currentData = state.typeaheadStreamingData[typeaheadId]

    if (!currentData) {
      // If data doesn't exist, ignore update (shouldn't happen, but safe guard)
      return state
    }

    const updatedData: App.TypeaheadStreamingData = { ...currentData }

    // Helper function to check if new timestamp is after existing timestamp
    const shouldUpdate = (existingTimestamp?: string): boolean => {
      if (!existingTimestamp) return true
      return timestamp > existingTimestamp
    }

    // Update each field with its data, timestamp, and input only if timestamp is newer
    if (extractedDataType === 'results' && shouldUpdate(currentData.results?.timestamp)) {
      updatedData.results = { data: updates.results ?? [], timestamp, input }
    }
    if (extractedDataType === 'naturalLanguageSuggestions' && shouldUpdate(currentData.naturalLanguageSuggestions?.timestamp)) {
      updatedData.naturalLanguageSuggestions = { data: updates.naturalLanguageSuggestions ?? [], timestamp, input }
    }
    if (extractedDataType === 'location' && shouldUpdate(currentData.location?.timestamp)) {
      updatedData.location = { data: updates.location, timestamp, input }
    }
    if (extractedDataType === 'property' && shouldUpdate(currentData.property?.timestamp)) {
      updatedData.property = { data: updates.property, timestamp, input }
    }
    if (extractedDataType === 'bedrooms' && shouldUpdate(currentData.bedrooms?.timestamp)) {
      updatedData.bedrooms = { data: updates.bedrooms, timestamp, input }
    }
    if (extractedDataType === 'amenities' && shouldUpdate(currentData.amenities?.timestamp)) {
      updatedData.amenities = { data: updates.amenities, timestamp, input }
    }
    if (extractedDataType === 'inclusions' && shouldUpdate(currentData.inclusions?.timestamp)) {
      updatedData.inclusions = { data: updates.inclusions, timestamp, input }
    }
    if (extractedDataType === 'tags' && shouldUpdate(currentData.tags?.timestamp)) {
      updatedData.tags = { data: updates.tags, timestamp, input }
    }
    if (extractedDataType === 'chains' && shouldUpdate(currentData.chains?.timestamp)) {
      updatedData.chains = { data: updates.chains, timestamp, input }
    }
    if (extractedDataType === 'sortBy' && shouldUpdate(currentData.sortBy?.timestamp)) {
      updatedData.sortBy = { data: updates.sortBy, timestamp, input }
    }
    if (extractedDataType === 'travelDates' && shouldUpdate(currentData.travelDates?.timestamp)) {
      updatedData.travelDates = { data: updates.travelDates, timestamp, input }
    }
    if (extractedDataType === 'occupancy' && shouldUpdate(currentData.occupancy?.timestamp)) {
      updatedData.occupancy = { data: updates.occupancy, timestamp, input }
    }
    if (extractedDataType === 'price' && shouldUpdate(currentData.price?.timestamp)) {
      updatedData.price = { data: updates.price, timestamp, input }
    }
    if (extractedDataType === 'finalSearchItem' && shouldUpdate(currentData.finalSearchItem?.timestamp)) {
      updatedData.finalSearchItem = { data: updates.finalSearchItem, timestamp, input }
    }

    return {
      ...state,
      typeaheadStreamingData: {
        ...state.typeaheadStreamingData,
        [typeaheadId]: updatedData,
      },
    }
  },
  [TYPEAHEAD_STREAMING_COMPLETE]: (state, action) => {
    const { typeaheadId, input } = action
    const currentData = state.typeaheadStreamingData[typeaheadId]

    if (!currentData) {
      return state
    }

    return {
      ...state,
      typeaheadStreamingData: {
        ...state.typeaheadStreamingData,
        [typeaheadId]: {
          ...currentData,
          isComplete: true,
          completedInput: input,
        },
      },
    }
  },
  [TYPEAHEAD_STREAMING_ERROR]: (state, action) => {
    const { typeaheadId } = action
    const currentData = state.typeaheadStreamingData[typeaheadId]

    if (!currentData) {
      return state
    }

    return {
      ...state,
      typeaheadStreamingData: {
        ...state.typeaheadStreamingData,
        [typeaheadId]: {
          ...currentData,
          isComplete: false,
        },
      },
    }
  },
  [SET_TYPEAHEAD_STREAMING_SOCKET_STATUS]: (state, action) => {
    const { typeaheadId, status } = action
    const currentData = state.typeaheadStreamingData[typeaheadId]

    if (!currentData) {
      // Initialize with required fields if data doesn't exist yet
      return {
        ...state,
        typeaheadStreamingData: {
          ...state.typeaheadStreamingData,
          [typeaheadId]: {
            typeaheadId,
            socketListenersStatus: status,
            isComplete: false,
          },
        },
      }
    }

    return {
      ...state,
      typeaheadStreamingData: {
        ...state.typeaheadStreamingData,
        [typeaheadId]: {
          ...currentData,
          socketListenersStatus: status,
        },
      },
    }
  },
  [SET_TYPEAHEAD_USER_SELECTED_DATES]: (state, action) => {
    const { typeaheadId, dates } = action
    const currentData = state.typeaheadStreamingData[typeaheadId]

    if (!currentData) {
      return {
        ...state,
        typeaheadStreamingData: {
          ...state.typeaheadStreamingData,
          [typeaheadId]: {
            typeaheadId,
            isComplete: false,
            userSelectedDates: dates,
          },
        },
      }
    }

    return {
      ...state,
      typeaheadStreamingData: {
        ...state.typeaheadStreamingData,
        [typeaheadId]: {
          ...currentData,
          userSelectedDates: dates,
        },
      },
    }
  },
  [SET_TYPEAHEAD_USER_SELECTED_OCCUPANCY]: (state, action) => {
    const { typeaheadId, occupancy } = action
    const currentData = state.typeaheadStreamingData[typeaheadId]

    if (!currentData) {
      return {
        ...state,
        typeaheadStreamingData: {
          ...state.typeaheadStreamingData,
          [typeaheadId]: {
            typeaheadId,
            isComplete: false,
            userSelectedOccupancy: occupancy,
          },
        },
      }
    }

    return {
      ...state,
      typeaheadStreamingData: {
        ...state.typeaheadStreamingData,
        [typeaheadId]: {
          ...currentData,
          userSelectedOccupancy: occupancy,
        },
      },
    }
  },
})
